"use client";

import Link from "next/link";
import { PAGE_TITLES } from "@/lib/pageTitles";
import { usePathname } from "next/navigation";
import { BookOpen, Clapperboard, Cpu, Ellipsis, FileText, Film, FolderOpen, PanelLeftClose, PanelLeftOpen, Sprout, WandSparkles } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { ComfyUiIcon } from "@/components/icons/ComfyUiIcon";

// 라벨은 브라우저 탭 제목과 같은 값을 쓴다(lib/pageTitles.ts). 한쪽만 고쳐 어긋나지 않게.
const navItems = [
  { href: "/plot/explorer", label: PAGE_TITLES["/plot/explorer"], icon: FolderOpen },
  { href: "/trope", label: PAGE_TITLES["/trope"], icon: BookOpen },
  { href: "/material", label: PAGE_TITLES["/material"], icon: Sprout },
  { href: "/reel", label: PAGE_TITLES["/reel"], icon: Clapperboard },
  { href: "/ltx", label: PAGE_TITLES["/ltx"], icon: FileText },
  { href: "/project", label: PAGE_TITLES["/project"], icon: WandSparkles },
  { href: "/shots", label: PAGE_TITLES["/shots"], icon: Film },
  { href: "/workers", label: PAGE_TITLES["/workers"], icon: Cpu },
  { href: "/comfylab", label: PAGE_TITLES["/comfylab"], icon: ComfyUiIcon }
];
const mobileNavItems = navItems.filter((item) => item.label === "Plots" || item.label === "Tropes" || item.label === "Materials" || item.label === "Shots");

// GNB 마지막 '기타(···)' — 후버 시 우측으로 팝아웃되는 임시·실험 페이지 모음.
const moreNavItems = [
  { href: "/conflict-collector", label: PAGE_TITLES["/conflict-collector"] },
  { href: "/trope-collector", label: PAGE_TITLES["/trope-collector"] },
  { href: "/face-metrics", label: "얼굴 지표" },
  { href: "/face-adjust", label: "얼굴 조정" },
  { href: "/bench-face-prompt", label: "Face Prompts Bench" },
  { href: "/bench-face-age", label: "Face Aging Bench" },
  { href: "/test-face-prepare", label: "Face Preparing Test" },
  { href: "/test-face-prompts", label: "Face Prompts Test" }
];

const SIDEBAR_STORAGE_KEY = "story37.sidebarCollapsed";

type EnvironmentSwitch = {
  label: "dev" | "prod";
  origin: string;
};

function environmentSwitchForLocation(location: Pick<Location, "hostname" | "port" | "protocol">): EnvironmentSwitch | null {
  if (location.protocol === "https:" && location.hostname === "glc.cama-shade.ts.net") {
    return { label: "dev", origin: "http://100.100.1.8:3700" };
  }
  if (location.port === "3700") {
    return { label: "prod", origin: "https://glc.cama-shade.ts.net" };
  }
  if (location.port === "3710") {
    return { label: "dev", origin: "http://100.100.1.8:3700" };
  }
  return null;
}

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [environmentSwitch, setEnvironmentSwitch] = useState<EnvironmentSwitch | null>(null);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  // '기타' 팝아웃 좌표 — sidebar가 overflow:hidden이라 fixed로 띄운다.
  const [moreMenuAt, setMoreMenuAt] = useState<{ x: number; y: number } | null>(null);
  const moreCloseTimerRef = useRef<number | null>(null);
  const lastScrollTargetRef = useRef<EventTarget | null>(null);
  const lastScrollTopRef = useRef(0);
  const lastTouchYRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1");
    } catch {
      setCollapsed(false);
    }
    setEnvironmentSwitch(environmentSwitchForLocation(window.location));
  }, []);

  function switchEnvironment() {
    if (!environmentSwitch) {
      return;
    }
    const { pathname: currentPath, search, hash } = window.location;
    window.location.assign(`${environmentSwitch.origin}${currentPath}${search}${hash}`);
  }

  function openMoreMenu(element: HTMLElement) {
    if (moreCloseTimerRef.current !== null) {
      window.clearTimeout(moreCloseTimerRef.current);
      moreCloseTimerRef.current = null;
    }
    const rect = element.getBoundingClientRect();
    setMoreMenuAt({ x: rect.right + 6, y: rect.top });
  }

  function scheduleMoreMenuClose() {
    if (moreCloseTimerRef.current !== null) window.clearTimeout(moreCloseTimerRef.current);
    moreCloseTimerRef.current = window.setTimeout(() => setMoreMenuAt(null), 180);
  }

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      // Ignore storage failures; the sidebar still works for the current session.
    }

    document.documentElement.dataset.sidebarCollapsed = collapsed ? "true" : "false";
  }, [collapsed]);

  useEffect(() => {
    function scrollTopForTarget(target: EventTarget | null) {
      if (target instanceof HTMLElement) {
        return target.scrollTop;
      }
      return window.scrollY || document.documentElement.scrollTop || 0;
    }

    function handleScroll(event: Event) {
      const target = event.target;
      const currentTop = scrollTopForTarget(target);
      const sameTarget = target === lastScrollTargetRef.current;
      const previousTop = sameTarget ? lastScrollTopRef.current : 0;
      const delta = currentTop - previousTop;
      lastScrollTargetRef.current = target;
      lastScrollTopRef.current = currentTop;

      if (currentTop <= 8) {
        setMobileNavHidden(false);
      } else if (delta > 12) {
        setMobileNavHidden(true);
      } else if (delta < -12) {
        setMobileNavHidden(false);
      }
    }

    function handleTouchStart(event: TouchEvent) {
      lastTouchYRef.current = event.touches[0]?.clientY ?? null;
    }

    function handleTouchMove(event: TouchEvent) {
      const previousY = lastTouchYRef.current;
      const currentY = event.touches[0]?.clientY ?? null;
      if (previousY === null || currentY === null) {
        lastTouchYRef.current = currentY;
        return;
      }
      const deltaY = currentY - previousY;
      lastTouchYRef.current = currentY;
      if (Math.abs(deltaY) < 10) {
        return;
      }
      if (deltaY < 0) {
        setMobileNavHidden(true);
      } else {
        setMobileNavHidden(false);
      }
    }

    function handleTouchEnd() {
      lastTouchYRef.current = null;
    }

    document.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    document.addEventListener("touchstart", handleTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", handleTouchMove, { capture: true, passive: true });
    document.addEventListener("touchend", handleTouchEnd, { capture: true, passive: true });
    document.addEventListener("touchcancel", handleTouchEnd, { capture: true, passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("scroll", handleScroll, { capture: true });
      document.removeEventListener("touchstart", handleTouchStart, { capture: true });
      document.removeEventListener("touchmove", handleTouchMove, { capture: true });
      document.removeEventListener("touchend", handleTouchEnd, { capture: true });
      document.removeEventListener("touchcancel", handleTouchEnd, { capture: true });
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (pathname === "/face-metrics" || pathname === "/face-adjust") return <>{children}</>;

  return (
    <div className={`shell ${collapsed ? "sidebarCollapsed" : ""}`}>
      <input
        className="sidebarToggleInput"
        id="story37-sidebar-toggle"
        type="checkbox"
        checked={collapsed}
        onChange={(event) => setCollapsed(event.currentTarget.checked)}
        aria-label="Toggle sidebar"
      />
      <aside className="sidebar" aria-label="Primary navigation">
        <div className="sidebarTop">
          <Link className="brand" href="/shots" aria-label="story37 home">
            <span className="brandText">story37</span>
          </Link>
          <label
            className="iconButton"
            htmlFor="story37-sidebar-toggle"
            aria-label="Toggle sidebar"
            title="Toggle sidebar"
          >
            <PanelLeftClose className="collapseIcon" size={18} />
            <PanelLeftOpen className="expandIcon" size={18} />
          </label>
        </div>

        <nav className="navList">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
            return (
              <Link
                className={`navItem ${active ? "active" : ""}`}
                href={item.href}
                key={item.href}
                title={item.label}
              >
                <Icon size={19} />
                <span>{item.label}</span>
              </Link>
            );
          })}
          <div
            className={`navItem navMore ${moreNavItems.some((item) => pathname === item.href || pathname.startsWith(`${item.href}/`)) ? "active" : ""}`}
            onMouseEnter={(event) => openMoreMenu(event.currentTarget)}
            onMouseLeave={scheduleMoreMenuClose}
            title="기타"
          >
            <Ellipsis size={19} />
            <span>기타</span>
          </div>
        </nav>

        <label
          className="sidebarBlankToggle"
          htmlFor="story37-sidebar-toggle"
          aria-label="Toggle sidebar"
          title="Toggle sidebar"
        />
        {environmentSwitch ? (
          <button
            className="environmentSwitchButton"
            onClick={switchEnvironment}
            title={`Switch to ${environmentSwitch.label}`}
            type="button"
          >
            {environmentSwitch.label}
          </button>
        ) : null}
      </aside>

      <nav
        className={`mobileGlobalNav ${mobileNavHidden ? "hidden" : ""}`}
        aria-label="Mobile navigation"
      >
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Link
              className={`mobileNavItem ${active ? "active" : ""}`}
              href={item.href}
              key={item.href}
              title={item.label}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="content">{children}</div>

      {moreMenuAt ? (
        <nav
          aria-label="기타 페이지"
          className="navMoreMenu"
          onMouseEnter={() => {
            if (moreCloseTimerRef.current !== null) {
              window.clearTimeout(moreCloseTimerRef.current);
              moreCloseTimerRef.current = null;
            }
          }}
          onMouseLeave={() => setMoreMenuAt(null)}
          style={{ left: moreMenuAt.x, top: moreMenuAt.y }}
        >
          {moreNavItems.map((item) => (
            <Link
              className={`navMoreItem ${pathname === item.href || pathname.startsWith(`${item.href}/`) ? "active" : ""}`}
              href={item.href}
              key={item.href}
              onClick={() => setMoreMenuAt(null)}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
